import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Sun, Moon, Monitor, Languages, LogOut, User, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TopBar() {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { user, logout, setAuth } = useAuth();

  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;

  const roleLabel = { advertiser: t("auth.advertiser"), publisher: t("auth.publisher"), admin: "Admin" };
  const otherRole = user?.role === "advertiser" ? "publisher" : "advertiser";
  const otherRoleLabel = user?.role === "advertiser" ? t("auth.publisher") : t("auth.advertiser");

  const handleSwitchRole = async () => {
    try {
      const res = await axios.post(`${API}/auth/switch-role`, { role: otherRole });
      // Update token i przeladuj
      if (res.data.token) {
        localStorage.setItem("epidemos-token", res.data.token);
        window.location.reload();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Blad zmiany roli"); }
  };

  return (
    <header data-testid="topbar"
      className="h-16 backdrop-blur-xl bg-background/80 border-b border-border sticky top-0 z-50 flex items-center justify-between px-6">
      <div className="flex items-center gap-4">
        <h2 className="text-sm font-medium text-muted-foreground font-body">AI Content Management System</h2>
      </div>

      <div className="flex items-center gap-2">
        {/* Jezyk */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="language-toggle"
              className="gap-2 text-muted-foreground hover:text-foreground">
              <Languages className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase">{language}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="lang-pl" onClick={() => setLanguage("pl")} className={language === "pl" ? "bg-accent" : ""}>Polski</DropdownMenuItem>
            <DropdownMenuItem data-testid="lang-en" onClick={() => setLanguage("en")} className={language === "en" ? "bg-accent" : ""}>English</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Motyw */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="theme-toggle" className="text-muted-foreground hover:text-foreground">
              <ThemeIcon className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")}><Sun className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />{t("settings.themeLight")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}><Moon className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />{t("settings.themeDark")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}><Monitor className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />{t("settings.themeSystem")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="user-menu"
                className="gap-2 text-muted-foreground hover:text-foreground">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-3 h-3 text-primary" strokeWidth={1.5} />
                </div>
                <span className="text-xs font-medium">{user.display_name || user.identifier}</span>
                <Badge variant="outline" className="text-[8px] rounded-md">{roleLabel[user.role] || user.role}</Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5">
                <p className="text-xs font-medium">{user.display_name || user.identifier}</p>
                <p className="text-[10px] text-muted-foreground">{user.identifier} ({user.role})</p>
              </div>
              <DropdownMenuSeparator />
              {user.role !== "admin" && (
                <DropdownMenuItem onClick={handleSwitchRole} className="gap-2 cursor-pointer">
                  <ArrowLeftRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Przelacz na: {otherRoleLabel}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-500">
                <LogOut className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                {t("auth.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
